import { useCallback, useEffect, useState } from "react";
import { LocaleCombobox } from "@wa/ui";
import { Loader2, Save } from "lucide-react";
import { useI18n, useT } from "../../../../i18n/I18nProvider";
import { hostSiteId, type StaffUser } from "../../../../lib/auth";
import {
  getCompany, getNumbering, saveCompany, saveNumbering,
  type CompanyInfo, type DocNumberType, type NumberingConfig, type NumberingPatch,
} from "../../../../lib/sales";
import {
  getViewRetention, saveViewRetention,
  type ViewRetentionDue, type ViewRetentionSettings,
} from "../../../../lib/salesViewRetention";
import { FALLBACK_DOC_LOCALES } from "../../../../lib/docLocales";
import { refreshInvoicingVat } from "../../../useInvoicingVat";
import { Btn, Card, CardHead, useToast } from "../../../ui";
import { ToggleRow } from "../panel/fields";
import { DOC_TYPES, NumberingCard } from "./NumberingCard";
import { DocLookCard } from "./DocLookCard";
import { SellerCard } from "./SellerCard";
import { ViewTrailCard } from "./ViewTrailCard";
import { Field, TextField } from "./fields";

const EMPTY_COMPANY: CompanyInfo = {
  name: "", regCode: "", vatNumber: "", email: "", phone: "",
  website: "", address: "", iban: "", bankName: "", footerNote: "",
  logoUrl: "", colorMode: "color", accentColor: "#2563eb", qrMode: "auto",
};

/**
 * The kinds whose term is worth an operator's attention. A credit note is not
 * one of them: it corrects an invoice and inherits that invoice's term, so the
 * server keeps it in step and the screen stays three fields instead of four.
 */
const TERM_TYPES = ["invoice", "estimate", "proposal"] as const;
type TermType = (typeof TERM_TYPES)[number];

const TERM_LABEL: Record<TermType, string> = {
  invoice: "settings.invoicing.dueDaysInvoice",
  estimate: "settings.invoicing.dueDaysEstimate",
  proposal: "settings.invoicing.dueDaysProposal",
};

/**
 * Mirrors the server's default policy, and only ever used before the first load
 * answers. Off, like the server's, so the card never draws a switch as on for
 * the moment it takes the request to come back.
 */
const FALLBACK_RETENTION: ViewRetentionSettings = { enabled: false, days: 180 };
const EMPTY_DUE: ViewRetentionDue = { due: 0, withTrail: 0, before: "" };

/** Mirrors the server's defaults, and only ever used before the first load answers. */
const FALLBACK_TERMS: Record<TermType, number> = { invoice: 7, estimate: 30, proposal: 14 };
const FALLBACK_TAX_PCT = 24;
/** Mirrors the server's default. Estonian, which is what every desk had before. */
const FALLBACK_DOC_LOCALE = "et";
const FALLBACK_VAT_ENABLED = true;

/**
 * Settings -> Invoices: number templates, the counter reset, the payment term
 * per document kind, whether VAT is charged at all and at what rate, how the
 * document looks and the seller's own details.
 *
 * Gated on `manage:crm.sales`, which is what the PUT behind every control here
 * checks (see `../perms.ts`).
 *
 * Four cards, one Save. The page holds all of the state and the panels are
 * display: a single button that writes both endpoints is the only shape that
 * matches how these settings are actually edited - nobody changes a number
 * template without checking what the preview does to it first.
 */
export function InvoiceSettings({ user }: { user: StaffUser }) {
  const t = useT();
  // The OPERATOR's language, used only to NAME the options. What the picker
  // chooses is the language documents are written in, which is a different
  // question with a different answer.
  const { locale: uiLocale } = useI18n();
  const toast = useToast();
  const siteId = hostSiteId(user);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [templates, setTemplates] = useState<Record<DocNumberType, string>>({} as Record<DocNumberType, string>);
  const [terms, setTerms] = useState<Record<TermType, number>>(FALLBACK_TERMS);
  const [taxRatePct, setTaxRatePct] = useState(FALLBACK_TAX_PCT);
  // Kept apart from the rate on purpose. Turning the switch off must not wipe
  // the number, so that registering for VAT later is one switch rather than a
  // rate somebody has to remember and type back in.
  const [vatEnabled, setVatEnabled] = useState(FALLBACK_VAT_ENABLED);
  const [docLocale, setDocLocale] = useState(FALLBACK_DOC_LOCALE);
  const [docLocales, setDocLocales] = useState<string[]>(FALLBACK_DOC_LOCALES);
  const [nextNums, setNextNums] = useState<Record<DocNumberType, number>>({} as Record<DocNumberType, number>);
  // Only the counters the operator actually touched are sent. A PUT that
  // repeated every current value would fight any number the server handed out
  // between the load and the save.
  const [nextDirty, setNextDirty] = useState<Set<DocNumberType>>(new Set());
  const [company, setCompany] = useState<CompanyInfo>(EMPTY_COMPANY);
  // The visitor trail policy answers a different endpoint from the two above and
  // is still saved by the same button - four cards, one Save, as the tab has
  // always worked. Kept beside its loaded value so the PUT can be skipped
  // entirely when nothing about it moved.
  const [retention, setRetention] = useState<ViewRetentionSettings>(FALLBACK_RETENTION);
  const [retentionLoaded, setRetentionLoaded] = useState<ViewRetentionSettings>(FALLBACK_RETENTION);
  const [retentionDue, setRetentionDue] = useState<ViewRetentionDue>(EMPTY_DUE);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [num, comp, trail] = await Promise.all([getNumbering(), getCompany(), getViewRetention()]);
      setTemplates(num.templates);
      setTerms(readTerms(num));
      setTaxRatePct(num.defaultTaxRatePct ?? FALLBACK_TAX_PCT);
      setVatEnabled(num.vatEnabled ?? FALLBACK_VAT_ENABLED);
      setDocLocale(num.docLocale ?? FALLBACK_DOC_LOCALE);
      // An older API answers without the list. Falling back to the two that
      // ship in the bundle keeps the picker usable rather than empty.
      setDocLocales(num.docLocales?.length ? num.docLocales : FALLBACK_DOC_LOCALES);
      setNextNums(readNext(num.sequences));
      setNextDirty(new Set());
      setCompany({ ...EMPTY_COMPANY, ...comp });
      setRetention(trail.settings);
      setRetentionLoaded(trail.settings);
      setRetentionDue(trail.due);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  function setTemplate(doc: DocNumberType, template: string) {
    setTemplates((prev) => ({ ...prev, [doc]: template }));
  }

  function setTerm(doc: TermType, days: number) {
    setTerms((prev) => ({ ...prev, [doc]: days }));
  }

  function setNext(doc: DocNumberType, next: number) {
    setNextNums((prev) => ({ ...prev, [doc]: next }));
    setNextDirty((prev) => new Set(prev).add(doc));
  }

  function patchCompany(patch: Partial<CompanyInfo>) {
    setCompany((prev) => ({ ...prev, ...patch }));
  }

  function patchRetention(patch: Partial<ViewRetentionSettings>) {
    setRetention((prev) => ({ ...prev, ...patch }));
  }

  async function save() {
    setSaving(true);
    try {
      const next: Partial<Record<DocNumberType, number>> = {};
      for (const doc of nextDirty) next[doc] = nextNums[doc];
      // `dueDaysByType` and not the older flat `dueDays`: the server still
      // accepts the flat field, but it only ever reaches the invoice, so a form
      // that sent it would drop the estimate and the proposal terms it is
      // holding without saying so.
      const patch: NumberingPatch = {
        templates,
        dueDaysByType: terms,
        defaultTaxRatePct: taxRatePct,
        vatEnabled,
        docLocale,
        next: Object.keys(next).length ? next : undefined,
      };
      // The retention PUT is sent only when the policy actually moved. It writes
      // an audit row, and a Save pressed after editing a number template must not
      // read, in the log, as somebody having touched the erasure schedule.
      const trailMoved =
        retention.enabled !== retentionLoaded.enabled || retention.days !== retentionLoaded.days;
      await Promise.all([
        saveNumbering(patch),
        saveCompany(company),
        ...(trailMoved ? [saveViewRetention(retention)] : []),
      ]);
      // Every sales screen caches this switch for the life of the tab, so a
      // document left open in another route would otherwise keep drawing the
      // column this save just turned off.
      refreshInvoicingVat();
      await load();
      toast(t("settings.invoicing.saved"));
    } catch {
      toast(t("settings.invoicing.saveError"));
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="crm-card flex justify-center py-16 text-muted-foreground">
        <Loader2 className="size-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <NumberingCard templates={templates} nextNums={nextNums} onTemplate={setTemplate} onNext={setNext} />

      <Card>
        <CardHead title={t("settings.invoicing.defaults")} />
        <div className="px-6 pb-6 pt-1">
          {/* The group is named once and the three fields under it carry only
              the kind: three boxes each captioned "payment term" would read as
              one field printed three times. Small caps rather than a heavier
              weight, which is how `../panel/fields` already captions a group of
              fields - the caption has to be told apart from the labels under
              it, not shouted over them. */}
          <h4 className="crm-lab uppercase tracking-[0.07em]">{t("settings.invoicing.dueDays")}</h4>
          <div className="grid gap-x-5 sm:grid-cols-3">
            {TERM_TYPES.map((doc) => (
              <TextField
                key={doc}
                label={t(TERM_LABEL[doc])}
                type="number"
                min="0"
                inputMode="numeric"
                numeric
                value={String(terms[doc])}
                onChange={(v) => setTerm(doc, parseInt(v, 10) || 0)}
              />
            ))}
          </div>
          {/* One note under the row rather than three copies of the same
              sentence: it describes the group, not any one kind. */}
          <p className="crm-fnote">{t("settings.invoicing.dueDaysHint")}</p>

          {/* The switch above the rate, because it decides whether the rate is
              a question at all. Hidden rather than greyed when it is off: a
              disabled box still asks the reader what belongs in it. */}
          <div className="mt-5 border-t border-[var(--crm-hair)] pt-5">
            <ToggleRow
              label={t("settings.invoicing.vatEnabled")}
              hint={t("settings.invoicing.vatEnabledHint")}
              checked={vatEnabled}
              onChange={setVatEnabled}
            />
          </div>

          {/* Under the VAT block and above the notes, because it is the same
              sort of question: what a document says before anybody types into
              it. The hint carries the whole chain, since the answer here is
              only reached for a client who has none of their own. */}
          <div className="mt-5 border-t border-[var(--crm-hair)] pt-5">
            <Field className="max-w-xs" label={t("settings.invoicing.docLocale")} hint={t("settings.invoicing.docLocaleHint")}>
              <LocaleCombobox
                className="h-[52px] border-0 px-[18px] text-[13.5px] shadow-none"
                popoverClassName="rounded-2xl"
                locales={docLocales}
                displayLocale={uiLocale}
                value={docLocale}
                onChange={setDocLocale}
                aria-label={t("settings.invoicing.docLocale")}
                searchPlaceholder={t("common.localeSearch")}
                emptyLabel={t("common.localeNone")}
              />
            </Field>
          </div>

          {vatEnabled ? (
            <TextField
              className="max-w-xs"
              label={t("settings.invoicing.defaultTaxRatePct")}
              hint={t("settings.invoicing.taxRateHint")}
              type="number"
              min="0"
              inputMode="numeric"
              numeric
              value={String(taxRatePct)}
              onChange={(v) => setTaxRatePct(parseInt(v, 10) || 0)}
            />
          ) : (
            <p className="crm-fnote mt-3">{t("settings.invoicing.vatOffNote")}</p>
          )}
        </div>
      </Card>

      <DocLookCard company={company} siteId={siteId} onChange={patchCompany} />
      <SellerCard company={company} onChange={patchCompany} />
      <ViewTrailCard settings={retention} due={retentionDue} onChange={patchRetention} />

      <div className="flex justify-end">
        <Btn tone="dark" onClick={() => void save()} disabled={saving}>
          {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
          {t(saving ? "settings.invoicing.saving" : "settings.invoicing.save")}
        </Btn>
      </div>
    </div>
  );
}

/**
 * The term per kind out of the settings response.
 *
 * `dueDays` is the fallback for the invoice and not a duplicate: an API that
 * predates the per-type terms answers with that field alone, and it has always
 * meant the invoice term. Whatever it cannot answer for falls back to the same
 * numbers the server would have used.
 *
 * Read through `Partial` although the type says otherwise: this is JSON off the
 * wire, and a browser holding a cached bundle can outlive the server version it
 * was built against. The type describes what the current API promises; the
 * fallbacks describe what a screen does when it is talking to an older one.
 */
function readTerms(num: NumberingConfig): Record<TermType, number> {
  const byType: Partial<Record<DocNumberType, number>> = num.dueDaysByType ?? {};
  return {
    invoice: byType.invoice ?? num.dueDays ?? FALLBACK_TERMS.invoice,
    estimate: byType.estimate ?? FALLBACK_TERMS.estimate,
    proposal: byType.proposal ?? FALLBACK_TERMS.proposal,
  };
}

/** The next number per kind, defaulting to 1 for a kind the server has not
 *  issued anything for yet. */
function readNext(sequences: NumberingConfig["sequences"]): Record<DocNumberType, number> {
  return Object.fromEntries(DOC_TYPES.map((doc) => [doc, sequences[doc]?.next ?? 1])) as Record<
    DocNumberType,
    number
  >;
}
